import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { User as UserIcon } from 'lucide-react';

const ProfilePage = () => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || '',
    phone: user?.phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    try {
      const res = await api.put('/profile', form);
      setUser?.(res.data);
      toast({ title: 'Profile updated' });
    } catch (error) {
      if (error?.errors) {
        setErrors(error.errors);
      }
      toast({
        title: 'Failed to update profile',
        description: error?.error?.message || error?.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold">Profile</h1>
      </div>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-blue-600 flex items-center justify-center text-white text-xl font-semibold shrink-0">
            {user?.logoUrl ? (
              <img src={user.logoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              user?.name?.charAt(0).toUpperCase() || <UserIcon className="w-6 h-6" />
            )}
          </div>
          <div>
            <p className="font-semibold text-lg">{user?.name}</p>
            <p className="text-sm text-gray-500">{user?.role === 'staff' ? 'Staff' : 'Organizer'}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="profileName">Full Name *</Label>
            <Input id="profileName" value={form.name} onChange={handleChange('name')} />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name[0]}</p>}
          </div>
          <div>
            <Label htmlFor="profileEmail">Email *</Label>
            <Input id="profileEmail" type="email" value={form.email} onChange={handleChange('email')} />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email[0]}</p>}
          </div>
          <div>
            <Label htmlFor="profilePhone">Contact No.</Label>
            <Input id="profilePhone" value={form.phone} onChange={handleChange('phone')} />
          </div>
          <div>
            <Label htmlFor="profileCompany">Company</Label>
            <Input id="profileCompany" value={form.company} onChange={handleChange('company')} />
          </div>
        </div>

        <Button
          className="bg-orange-500 hover:bg-orange-600"
          onClick={handleSave}
          disabled={saving || !form.name || !form.email}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Card>
    </div>
  );
};

export default ProfilePage;
